import { Component, ElementRef, ViewChild, OnInit } from '@angular/core';
import { AABB } from '../../geometry';
import { OverlayStatus } from 'src/app/_bio-check/face-check/components/overlay/overlay-status.enum';

const HALF_PI = Math.PI / 2;
const HALF_GAP = Math.PI / 16;

@Component({
  selector: 'app-overlay',
  templateUrl: './overlay.component.html',
  styleUrls: ['./overlay.component.scss'],
  standalone: false
})
export class OverlayComponent implements OnInit {

  @ViewChild('overlay_bg', { static: true }) overlay_bg!: ElementRef<HTMLCanvasElement>;
  @ViewChild('overlay_fg', { static: true }) overlay_fg!: ElementRef<HTMLCanvasElement>;

  public roi: AABB = new AABB(0, 0, 0, 0);
  private _status: OverlayStatus = OverlayStatus.Loading;
  get status() {
    return this._status;
  }
  set status(newStatus: OverlayStatus) {
    this._status = newStatus;
    this.redrawForeground();
  }

  private animationBaseVal = 0;
  private animationCurrentVal = 0;
  private animationStartTime = 0;
  private animationDuration = 0;
  private animationRequest = 0;

  private fillStyleMask = "";
  private strokeStyleInitial = "";
  private strokeStyleFaceDetected = "";
  private strokeStyleError = "";


  public ngOnInit(): void {
    const style: CSSStyleDeclaration = window.getComputedStyle(this.overlay_fg.nativeElement);
    this.fillStyleMask = style.getPropertyValue('--mask-color');
    this.strokeStyleInitial = style.getPropertyValue('--roi-color-initial');
    this.strokeStyleFaceDetected = style.getPropertyValue('--roi-color-face-found');
    this.strokeStyleError = style.getPropertyValue('--roi-color-error');
    this.onResize();
  }

  public onResize(): void {
    const canvasBack: HTMLCanvasElement = this.overlay_bg.nativeElement;
    const canvasFore: HTMLCanvasElement = this.overlay_fg.nativeElement;
    canvasFore.width = canvasFore.scrollWidth;
    canvasFore.height = canvasFore.scrollHeight;
    canvasBack.width = canvasFore.scrollWidth;
    canvasBack.height = canvasFore.scrollHeight;
    this.redrawBackground();
    this.redrawForeground();
  }

  //#region Animation

  public setProgressAmount(amount: number): void {
    this.animationBaseVal = this.animationCurrentVal;
    this.animationCurrentVal = amount;
    this.animationStartTime = new Date().getTime();
    this.animationDuration = 250;
    this.requestAnimationFrame();
  }

  private requestAnimationFrame(): void {
    this.animationRequest = requestAnimationFrame(() => {
      this.animationRequest = 0;
      this.redrawForeground();
    });
  }

  public stopAnimation(): void {
    this.animationBaseVal = 0;
    this.animationCurrentVal = 0;
    this.animationStartTime = 0;
    this.animationDuration = 0;
    if (this.animationRequest) {
      cancelAnimationFrame(this.animationRequest);
      this.animationRequest = 0;
    }
  }

  //#endregion Animation


  //#region Drawing

  private redrawBackground(): void {
    const canvas = this.overlay_bg.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw Error("Cannot redraw background. No 2d context.");
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.beginPath();
    ctx.fillStyle = this.fillStyleMask;
    ctx.rect(0, 0, canvas.width, canvas.height);
    ctx.ellipse(this.roi.centerX, this.roi.centerY,
                this.roi.width / 2, this.roi.height / 2,
                0, 0, Math.PI * 2);
    ctx.fill('evenodd');
  }

  private clamp(num: number, min: number, max: number) {
    return num <= min ? min : num >= max ? max : num;
  }

  private redrawForeground(): void {

    // Calculate the progress fill
    let v = 1;
    if (this.animationStartTime) {
      v = (new Date().getTime() - this.animationStartTime) / this.animationDuration;
      v = Math.min(1.0, v);
      v = this.smoothingFunction(v);
      if (v < 1.0) {
        this.requestAnimationFrame();
      }
      v = this.animationBaseVal + v * (this.animationCurrentVal - this.animationBaseVal);
    }

    // va[4] = the amount (0 to 1) that each arc should be filled with its opaque color
    v *= 4;
    const va = [
      this.clamp(v - 0, 0, 1),
      this.clamp(v - 1, 0, 1),
      this.clamp(v - 2, 0, 1),
      this.clamp(v - 3, 0, 1),
    ];


    // Clear the whole canvas
    const canvas: HTMLCanvasElement = this.overlay_fg.nativeElement;
    const ctx: CanvasRenderingContext2D = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Prepare to draw the 4 arcs in the correct colour and line width and style
    switch (this.status) {
      case OverlayStatus.Loading:
      case OverlayStatus.Initial:
        ctx.strokeStyle = this.strokeStyleInitial;
        break;
      case OverlayStatus.TimedOut:
        ctx.strokeStyle = this.strokeStyleError;
        break;
      default:
        ctx.strokeStyle = this.strokeStyleFaceDetected;
    }
    ctx.lineWidth = 10;
    ctx.lineCap = 'round';

    // Iterate the 4 arcs
    const radiusX = (this.roi.width + ctx.lineWidth) / 2;
    const radiusY = (this.roi.height + ctx.lineWidth) / 2;
    for (let i = 0 ; i < 4 ; i++) {
      const fromAngle = (i - 1) * HALF_PI + HALF_GAP;
      const toAngle = i * HALF_PI - HALF_GAP;

      // If arc is not fully opaque, draw the full arc translucently first
      if (va[i] < 1) {
        ctx.globalAlpha = 0.32;
        ctx.beginPath();
        ctx.ellipse(this.roi.centerX, this.roi.centerY,
                    radiusX, radiusY,
                    0, fromAngle, toAngle);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      // Draw the opaque portion of arc
      if (va[i] > 0) {
        ctx.beginPath();
        ctx.ellipse(this.roi.centerX, this.roi.centerY,
                    radiusX, radiusY,
                    0, fromAngle, fromAngle + (toAngle - fromAngle) * va[i]);
        ctx.stroke();
      }
    }
  }

  //#endregion Drawing


  /**
   * The mathematical function f: [0, 1] --> [0, 1], f(x) = (sin((x + 3 / 2) * PI) + 1)/2, scales the time parameter t
   * and implements a ease-in/ease-out effect.
   * @param t curve parameter (0 < t < 1)
   */
  private smoothingFunction(t: number) {
    return ( Math.sin( (t + 3 / 2) * Math.PI ) + 1 ) / 2;
  }


}
